import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Course {
  id: string
  name: string
  category: string
  duration_hours: number
  format: string
  level: string
  is_mandatory: boolean
  has_certification: boolean
}

interface Enrollment {
  id: string
  course_name: string
  employee_name: string
  status: string
  progress_percent: number
  score: number | null
  enrolled_date: string
}

export default function TrainingPage() {
  const [courses, setCourses] = useState<Course[]>([])
  const [enrollments, setEnrollments] = useState<Enrollment[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [coursesRes, enrollRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/training/courses'),
      fetch(API_BASE + '/training/enrollments'),
      fetch(API_BASE + '/training/statistics')
    ])
    if (coursesRes.ok) setCourses((await coursesRes.json()).courses || [])
    if (enrollRes.ok) setEnrollments((await enrollRes.json()).enrollments || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800'
      case 'in_progress': return 'bg-blue-100 text-blue-800'
      case 'enrolled': return 'bg-yellow-100 text-yellow-800'
      case 'failed': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Training & Learning</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Add Course</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Courses</div>
              <div className="text-2xl font-bold">{statistics.total_courses}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">In Progress</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.in_progress}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Completed</div>
              <div className="text-2xl font-bold text-green-600">{statistics.completed}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Completion Rate</div>
              <div className="text-2xl font-bold">{statistics.completion_rate}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Hours</div>
              <div className="text-2xl font-bold text-purple-600">{statistics.total_learning_hours}h</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Recent Enrollments</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {enrollments.slice(0, 12).map(enr => (
                  <tr key={enr.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{enr.employee_name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{enr.course_name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${enr.progress_percent}%` }} />
                        </div>
                        <span className="text-sm">{enr.progress_percent}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(enr.status)}`}>
                        {enr.status.replace(/_/g, ' ')}
                      </span>
                      {enr.score && <span className="ml-2 text-xs text-gray-500">Score: {enr.score}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Available Courses</h3>
            <div className="space-y-3">
              {courses.map(course => (
                <div key={course.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm">{course.name}</span>
                    {course.is_mandatory && <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-800">Required</span>}
                  </div>
                  <div className="text-xs text-gray-500">{course.category} • {course.duration_hours}h • {course.level}</div>
                  <div className="flex gap-2 mt-2">
                    <span className="px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700">{course.format}</span>
                    {course.has_certification && <span className="px-2 py-0.5 rounded text-xs bg-green-50 text-green-700">Certified</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
